import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";

import CrmShell from "../components/CrmShell";
import {
  CrmPageHeader,
  CrmPanel,
  OwnerCell,
  RoleChip,
  StatusChip,
  crmUi,
} from "../components/CrmUi";
import { organizations } from "../demoData";

const tabs = [
  ["Alla", 248],
  ["Våra kunder", 96],
  ["Prospekt", 29],
  ["Partners", 54],
  ["Leverantörer", 32],
  ["Nätverksorganisationer", 92],
];

const CrmOrganizationsPage = () => (
  <CrmShell>
    <CrmPageHeader
      title="Organisationer"
      subtitle="Kunder, prospekt, partners och leverantörer i samma organisationsregister."
      actions={[
        <Button key="export" size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={{ textTransform: "none" }}>
          Exportera
        </Button>,
        <Button key="new" size="small" variant="contained" startIcon={<AddOutlinedIcon />} sx={{ textTransform: "none" }}>
          Ny organisation
        </Button>,
      ]}
    />

    <CrmPanel sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 1.4, pt: 1.25, borderBottom: `1px solid ${crmUi.border}` }}>
        <Stack direction="row" spacing={1.5} sx={{ overflowX: "auto", pb: 0.9 }}>
          {tabs.map(([label, count], index) => (
            <Stack
              key={label}
              direction="row"
              spacing={0.45}
              alignItems="center"
              sx={{
                pb: 0.75,
                borderBottom: index === 0 ? "2px solid" : "2px solid transparent",
                borderColor: index === 0 ? "primary.main" : "transparent",
                whiteSpace: "nowrap",
              }}
            >
              <Typography sx={{ color: index === 0 ? crmUi.text : crmUi.muted, fontSize: 10.7, fontWeight: index === 0 ? 550 : 470 }}>
                {label}
              </Typography>
              <Typography sx={{ color: crmUi.subtle, fontSize: 9.4 }}>{count}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={0.8}
        sx={{ p: 1.15, borderBottom: `1px solid ${crmUi.border}` }}
      >
        <TextField
          size="small"
          placeholder="Sök organisationer..."
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 17 }} />
              </InputAdornment>
            ),
          }}
        />
        {["Relation", "Affärsnätverk", "Ansvarig", "Status"].map(label => (
          <TextField key={label} size="small" select defaultValue="" sx={{ minWidth: 135 }}>
            <MenuItem value="">{label}</MenuItem>
          </TextField>
        ))}
        <Button variant="outlined" startIcon={<FilterListOutlinedIcon />} sx={{ textTransform: "none" }}>
          Fler filter
        </Button>
      </Stack>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1080 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fbfcfd" }}>
              {["Organisation", "Relation till UniQueue", "Affärsnätverk", "Tillhör", "Debiteras av", "Ansvarig", "Senaste aktivitet", "Status", ""].map(x => (
                <TableCell key={x} sx={{ py: 0.9, color: crmUi.muted, fontSize: 9.6, fontWeight: 550 }}>
                  {x}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.slice(0, 6).map(org => (
              <TableRow key={org.id} hover>
                <TableCell sx={{ py: 0.75 }}>
                  <Typography
                    component={RouterLink}
                    to={`/administration/crm/organizations/${org.id}`}
                    sx={{ color: crmUi.text, fontSize: 11.2, fontWeight: 520, textDecoration: "none" }}
                  >
                    {org.name}
                  </Typography>
                </TableCell>
                <TableCell><RoleChip value={org.relation} /></TableCell>
                <TableCell sx={{ fontSize: 10.5, color: crmUi.text }}>{org.network}</TableCell>
                <TableCell sx={{ fontSize: 10.5, color: crmUi.text }}>{org.parent}</TableCell>
                <TableCell sx={{ fontSize: 10.5, color: crmUi.text }}>{org.billedBy}</TableCell>
                <TableCell><OwnerCell name={org.owner} /></TableCell>
                <TableCell sx={{ fontSize: 10.2, color: crmUi.muted }}>{org.lastActivity}</TableCell>
                <TableCell><StatusChip status={org.status} /></TableCell>
                <TableCell align="right">
                  <IconButton size="small"><MoreVertOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.4, py: 1 }}>
        <Typography sx={{ color: crmUi.muted, fontSize: 9.8 }}>Visar 1–6 av 248 organisationer</Typography>
        <Stack direction="row" spacing={0.4}>
          {["1", "2", "3", "…", "42"].map(x => (
            <Button key={x} size="small" variant={x === "1" ? "outlined" : "text"} sx={{ minWidth: 28, px: 0.5, fontSize: 9.8 }}>
              {x}
            </Button>
          ))}
        </Stack>
      </Stack>
    </CrmPanel>
  </CrmShell>
);

export default CrmOrganizationsPage;
